import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  reportsApi,
  type SafetyCaseRecommendation,
  type SafetyCaseRecommendationCorrectiveActionSummary,
  type WorkflowCorrectiveAction,
} from '../services/reportsApi';
import {
  loadSiteFlowConditionFamily,
  resolveSiteFlowConditionTypeLabel,
} from '../domain/condition_families/siteFlowConditionFamily';
import type { WorkplaceConditionFields } from '../domain/workplaceCondition/workplaceCondition';
import {
  formatControlStatusLabel,
  mapControlStatusFromApi,
  mapControlStatusToApi,
  mapSiteDecisionFromApi,
  mapSiteDecisionToApi,
  operatorNoteForApi,
  operatorVisibleNote,
  recommendationDisplayText,
  resolveControlReviewStatusLabel,
  resolveFollowUpSentence,
  selectMeasurementGuidanceRecommendation,
  selectPrimaryControlRecommendation,
  splitControlNoteAndReview,
  type ControlActionStatusUi,
  type ControlSiteDecisionUi,
} from '../domain/safetyCases/safetyCaseControlsPresenter';
import { formatSiteDate, formatSiteDateTime, formatSiteDateTimeLocalInput, isCalendarDateOnly, parseSiteDateTimeLocalInput } from '../domain/siteTime/siteTime';

type ControlDraft = {
  siteDecision: ControlSiteDecisionUi;
  decisionNote: string;
  description: string;
  owner: string;
  status: ControlActionStatusUi;
  dueDate: string;
  implementedAt: string;
  note: string;
  linkActionId: string;
  addRecommendationText: string;
  addRecommendationRationale: string;
  addRecommendationPriority: string;
};

const EMPTY_DRAFT: ControlDraft = {
  siteDecision: 'accepted',
  decisionNote: '',
  description: '',
  owner: '',
  status: 'planned',
  dueDate: '',
  implementedAt: '',
  note: '',
  linkActionId: '',
  addRecommendationText: '',
  addRecommendationRationale: '',
  addRecommendationPriority: 'medium',
};

const EMPTY_ACTIONS: Array<SafetyCaseRecommendationCorrectiveActionSummary | WorkflowCorrectiveAction> = [];
const EMPTY_RECOMMENDATIONS: SafetyCaseRecommendation[] = [];

function humanize(value: unknown, fallback = 'Not recorded'): string {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value?: string | null, fallback = 'Not recorded', timeZone?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return fallback;
  if (isCalendarDateOnly(text)) return formatSiteDate(text, timeZone) || text;
  return formatSiteDateTime(text, timeZone) || text;
}

function toDateFieldValue(value?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.slice(0, 10);
}

function toDateTimeLocalValue(value?: string | null, timeZone?: string | null): string {
  return formatSiteDateTimeLocalInput(value, timeZone);
}

function toImplementedAtIso(value?: string | null, timeZone?: string | null): string | undefined {
  return parseSiteDateTimeLocalInput(value, timeZone);
}

function pickRecordedAction(
  recommendation?: SafetyCaseRecommendation | null,
  recordedActions: Array<SafetyCaseRecommendationCorrectiveActionSummary | WorkflowCorrectiveAction> = EMPTY_ACTIONS,
): SafetyCaseRecommendationCorrectiveActionSummary | WorkflowCorrectiveAction | null {
  const linked = recommendation?.linked_corrective_action;
  const fromList = recordedActions.find((action) => {
    const status = String(action.status || '').trim().toLowerCase();
    return status === 'validated' || status === 'implemented' || status === 'completed';
  }) || recordedActions[0] || null;
  if (linked && (String(linked.title || '').trim() || String(linked.action_id || '').trim())) {
    const linkedId = String(linked.action_id || '').trim();
    const match = linkedId
      ? recordedActions.find((action) => String(action.action_id || '').trim() === linkedId)
      : undefined;
    return match ? { ...linked, ...match } : linked;
  }
  return fromList;
}

function buildDraft(
  recommendation?: SafetyCaseRecommendation | null,
  recordedAction?: SafetyCaseRecommendationCorrectiveActionSummary | WorkflowCorrectiveAction | null,
  timeZone?: string | null,
): ControlDraft {
  return {
    ...EMPTY_DRAFT,
    siteDecision: mapSiteDecisionFromApi(
      recommendation?.current_decision_state || recommendation?.latest_decision?.decision_type,
    ),
    decisionNote: String(recommendation?.latest_decision?.decision_notes || '').trim(),
    description: String(
      recordedAction?.title
      || recommendationDisplayText(recommendation)
      || '',
    ).trim(),
    owner: String(recordedAction?.assignee || '').trim(),
    status: mapControlStatusFromApi(recordedAction?.status),
    dueDate: toDateFieldValue(recordedAction?.due_date),
    implementedAt: toDateTimeLocalValue(
      (recordedAction as WorkflowCorrectiveAction | undefined)?.implemented_at
      || recordedAction?.implemented_at,
      timeZone,
    ),
    note: operatorVisibleNote(
      (recordedAction as WorkflowCorrectiveAction | undefined)?.verification_notes
      || recommendation?.latest_decision?.decision_notes
      || '',
    ),
  };
}

function controlReviewJsonForDetails(
  recommendation?: SafetyCaseRecommendation | null,
  recordedAction?: SafetyCaseRecommendationCorrectiveActionSummary | WorkflowCorrectiveAction | null,
): string | null {
  const candidates = [
    (recordedAction as WorkflowCorrectiveAction | undefined)?.verification_notes,
    recordedAction?.description,
    recommendation?.latest_decision?.decision_notes,
  ];
  for (const candidate of candidates) {
    const split = splitControlNoteAndReview(candidate);
    if (split.rawJson) return split.rawJson;
  }
  const review = (recordedAction as WorkflowCorrectiveAction | undefined)?.safety_control_review;
  if (review && typeof review === 'object') {
    try {
      return JSON.stringify({ safety_control_review: review }, null, 2);
    } catch {
      return null;
    }
  }
  return null;
}

interface Props {
  safetyCaseId: string;
  workflowId?: string | null;
  recommendations?: SafetyCaseRecommendation[];
  recordedActions?: Array<SafetyCaseRecommendationCorrectiveActionSummary | WorkflowCorrectiveAction>;
  latestFinalizedReportId?: string | null;
  workplaceCondition?: WorkplaceConditionFields | null;
  onCaseRefresh?: () => Promise<void> | void;
  readOnly?: boolean;
  correctiveActionsSlot?: React.ReactNode;
  followUpSlot?: React.ReactNode;
  followUpSummary?: string;
  hasControl?: boolean;
  onContinueMonitoring?: () => void;
  followupObservedCount?: unknown;
  directionalChange?: unknown;
  rateChangePercent?: unknown;
  measurementStatus?: unknown;
  measurementConfidence?: unknown;
  siteTimeZone?: string | null;
}

export const SafetyCaseRecommendationsSection: React.FC<Props> = ({
  safetyCaseId,
  workflowId,
  recommendations = EMPTY_RECOMMENDATIONS,
  recordedActions = EMPTY_ACTIONS,
  latestFinalizedReportId,
  workplaceCondition,
  onCaseRefresh,
  readOnly = false,
  correctiveActionsSlot,
  followUpSlot,
  followUpSummary,
  hasControl,
  onContinueMonitoring,
  followupObservedCount,
  directionalChange,
  rateChangePercent,
  measurementStatus,
  measurementConfidence,
  siteTimeZone = null,
}) => {
  const [items, setItems] = useState<SafetyCaseRecommendation[]>(recommendations);
  const [draft, setDraft] = useState<ControlDraft>(EMPTY_DRAFT);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);

  const primaryRecommendation = useMemo(
    () => selectPrimaryControlRecommendation(items),
    [items],
  );
  const measurementGuidance = useMemo(
    () => selectMeasurementGuidanceRecommendation(items),
    [items],
  );
  const recordedAction = useMemo(
    () => pickRecordedAction(primaryRecommendation, recordedActions),
    [primaryRecommendation, recordedActions],
  );
  const controlExists = Boolean(hasControl || recordedAction);
  const followUp = useMemo(
    () => resolveFollowUpSentence({
      hasControl: controlExists,
      followupObservedCount,
      directionalChange,
      rateChangePercent,
      measurementStatus,
      measurementConfidence,
    }),
    [
      controlExists,
      directionalChange,
      followupObservedCount,
      measurementConfidence,
      measurementStatus,
      rateChangePercent,
    ],
  );

  useEffect(() => {
    setItems(recommendations);
    setDraft(buildDraft(
      selectPrimaryControlRecommendation(recommendations),
      pickRecordedAction(selectPrimaryControlRecommendation(recommendations), recordedActions),
      siteTimeZone,
    ));
  }, [recommendations, recordedActions, siteTimeZone]);

  const siteFlowPack = useMemo(() => loadSiteFlowConditionFamily(), []);
  const conditionTypeLabel = useMemo(
    () => resolveSiteFlowConditionTypeLabel(workplaceCondition?.condition_type || null),
    [workplaceCondition?.condition_type],
  );
  const reviewJson = controlReviewJsonForDetails(primaryRecommendation, recordedAction);
  const reviewStatusLabel = resolveControlReviewStatusLabel({
    review: (recordedAction as WorkflowCorrectiveAction | undefined)?.safety_control_review,
    notes: (recordedAction as WorkflowCorrectiveAction | undefined)?.verification_notes
      || primaryRecommendation?.latest_decision?.decision_notes
      || recordedAction?.description,
  });
  const suggestedText = recommendationDisplayText(primaryRecommendation);
  const measurementText = recommendationDisplayText(measurementGuidance);

  const updateDraft = (patch: Partial<ControlDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const refreshFromServer = async () => {
    const response = await reportsApi.listSafetyCaseRecommendations(safetyCaseId);
    setItems(response.recommendations || []);
  };

  const finalizeMutation = async (message: string) => {
    await refreshFromServer();
    if (onCaseRefresh) await onCaseRefresh();
    setError(null);
    setSuccess(message);
    setToastOpen(message === 'Control saved');
  };

  const handleCreateRecommendation = async () => {
    const recommendationText = draft.addRecommendationText.trim();
    if (!recommendationText) {
      setError('Recommendation text is required.');
      return;
    }
    setPendingKey('create');
    setSuccess(null);
    try {
      await reportsApi.createSafetyCaseRecommendation(safetyCaseId, {
        recommendation_text: recommendationText,
        recommendation_type: 'manual_recommendation',
        priority: draft.addRecommendationPriority || 'medium',
        rationale: draft.addRecommendationRationale || 'Manual recommendation added from the Safety Case page.',
      });
      updateDraft({ addRecommendationText: '', addRecommendationRationale: '', addRecommendationPriority: 'medium' });
      await finalizeMutation('Recommendation added to the Safety Case.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to create the recommendation.');
    } finally {
      setPendingKey(null);
    }
  };

  const handleSaveControl = async () => {
    const recommendationId = String(primaryRecommendation?.recommendation_id || '').trim();
    const actionId = String(recordedAction?.action_id || '').trim();
    if (!draft.description.trim() && draft.siteDecision === 'accepted') {
      setError('Describe the action taken or control.');
      return;
    }
    if (draft.siteDecision === 'accepted' && draft.status === 'implemented' && !toImplementedAtIso(draft.implementedAt, siteTimeZone)) {
      setError('Implemented at is required when recording Control as implemented.');
      return;
    }
    setPendingKey('save-control');
    setSuccess(null);
    const operatorNote = operatorNoteForApi(draft.note);
    try {
      if (recommendationId) {
        const apiDecision = mapSiteDecisionToApi(draft.siteDecision);
        await reportsApi.recordSafetyCaseRecommendationDecision(safetyCaseId, recommendationId, {
          decision: apiDecision,
          decision_reason: draft.siteDecision === 'not_proceeding'
            ? (draft.decisionNote.trim() || 'Not proceeding')
            : (draft.decisionNote.trim() || undefined),
          decision_notes: operatorNote || draft.decisionNote.trim() || undefined,
        });
      }
      if (draft.siteDecision === 'accepted') {
        const dueDate = draft.dueDate ? `${draft.dueDate}T00:00:00Z` : undefined;
        const status = mapControlStatusToApi(draft.status);
        const implementedAt = draft.status === 'implemented' ? toImplementedAtIso(draft.implementedAt, siteTimeZone) : undefined;
        if (actionId) {
          await reportsApi.patchWorkflowCorrectiveAction(actionId, {
            title: draft.description.trim(),
            description: operatorNote,
            assignee: draft.owner.trim() || undefined,
            status: status as any,
            due_date: dueDate,
            ...(implementedAt ? { implemented_at: implementedAt } : {}),
            ...(operatorNote ? { verification_notes: operatorNote } : {}),
          });
          await finalizeMutation('Control saved');
          return;
        }
        if (recommendationId) {
          await reportsApi.createSafetyCaseRecommendationCorrectiveAction(safetyCaseId, recommendationId, {
            decision_reason: draft.decisionNote.trim() || 'Control recorded from the Safety Case.',
            decision_notes: operatorNote,
            corrective_action: {
              title: draft.description.trim(),
              description: operatorNote,
              assignee: draft.owner.trim() || undefined,
              priority: primaryRecommendation?.priority || 'medium',
              due_date: dueDate,
              action_type: primaryRecommendation?.recommendation_type || 'administrative',
              status,
              ...(implementedAt ? { implemented_at: implementedAt } : {}),
            },
          });
          await finalizeMutation('Control saved');
          return;
        }
        if (String(workflowId || '').trim()) {
          await reportsApi.createWorkflowCorrectiveAction(String(workflowId).trim(), {
            title: draft.description.trim(),
            description: operatorNote,
            assignee: draft.owner.trim() || undefined,
            status: status as any,
            due_date: dueDate,
            ...(implementedAt ? { implemented_at: implementedAt } : {}),
            ...(operatorNote ? { verification_notes: operatorNote } : {}),
          });
          await finalizeMutation('Control saved');
          return;
        }
        setError('A control recommendation is needed before a control can be recorded.');
        return;
      }
      await finalizeMutation(
        draft.siteDecision === 'not_proceeding'
          ? 'Decision recorded as not proceeding.'
          : 'Decision recorded.',
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to save the control.');
    } finally {
      setPendingKey(null);
    }
  };

  const handleLinkCorrectiveAction = async () => {
    const recommendationId = String(primaryRecommendation?.recommendation_id || '').trim();
    if (!recommendationId) return;
    if (!draft.linkActionId.trim()) {
      setError('An existing action is required to link.');
      return;
    }
    setPendingKey('link-action');
    setSuccess(null);
    try {
      await reportsApi.linkSafetyCaseRecommendationCorrectiveAction(safetyCaseId, recommendationId, {
        corrective_action_id: draft.linkActionId.trim(),
        decision_reason: draft.decisionNote.trim() || 'Existing control linked from the Safety Case.',
        decision_notes: operatorNoteForApi(draft.note),
      });
      updateDraft({ linkActionId: '' });
      await finalizeMutation('Existing control linked.');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Unable to link the existing control.');
    } finally {
      setPendingKey(null);
    }
  };

  const detailsSx = { '&:before': { display: 'none' }, boxShadow: 'none', bgcolor: 'transparent' };

  return (
    <Stack spacing={2} data-testid="safety-case-controls-sequence">
      <Typography variant="h6" sx={{ fontWeight: 800 }} data-testid="safety-case-controls-sequence-label">
        What did we decide to do about this safety problem?
      </Typography>
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {success ? (
        <Alert
          severity="success"
          data-testid="controls-save-success"
          action={success === 'Control saved' && onContinueMonitoring ? (
            <Button color="inherit" size="small" onClick={onContinueMonitoring}>
              Continue monitoring
            </Button>
          ) : undefined}
        >
          {success}
        </Alert>
      ) : null}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToastOpen(false)}>
          Control saved
        </Alert>
      </Snackbar>

      <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid="controls-suggested-action">
        <CardContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Suggested action</Typography>
          {suggestedText ? (
            <Typography variant="body2" color="text.secondary">{suggestedText}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="controls-empty-suggested">
              No suggested action is on file yet.
            </Typography>
          )}
          {measurementText ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="controls-monitoring-guidance">
              Monitoring guidance: {measurementText}
            </Typography>
          ) : null}
          <Accordion disableGutters elevation={0} sx={{ ...detailsSx, mt: 1 }} data-testid="controls-suggested-details">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.25}>
                {conditionTypeLabel ? (
                  <Typography variant="body2" color="text.secondary">
                    Condition type: {conditionTypeLabel}
                  </Typography>
                ) : null}
                {workplaceCondition?.why_this_matters_text ? (
                  <Typography variant="body2" color="text.secondary">
                    Operations relevance: {workplaceCondition.why_this_matters_text}
                  </Typography>
                ) : null}
                {workplaceCondition?.likely_contributing_condition ? (
                  <Typography variant="body2" color="text.secondary">
                    {String(workplaceCondition.contributing_condition_status || '').toLowerCase() === 'confirmed'
                      ? `Confirmed contributor: ${workplaceCondition.likely_contributing_condition}`
                      : `Reviewer hypothesis: ${workplaceCondition.likely_contributing_condition}`}
                  </Typography>
                ) : null}
                {siteFlowPack.suggested_corrective_actions.length ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Suggested site-flow action categories
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      {siteFlowPack.suggested_corrective_actions.map((label) => (
                        <Chip key={label} size="small" variant="outlined" label={humanize(label)} />
                      ))}
                    </Stack>
                  </Box>
                ) : null}
                {latestFinalizedReportId ? (
                  <Typography variant="body2" color="text.secondary">
                    Last included scan is on file.
                  </Typography>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid="controls-action-taken">
        <CardContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Action taken / control</Typography>
          {reviewStatusLabel ? (
            <Chip size="small" variant="outlined" label={reviewStatusLabel} sx={{ mb: 1.5 }} data-testid="controls-review-status" />
          ) : null}
          {!readOnly ? (
            <Stack spacing={1.5} data-testid="controls-action-taken-form">
              {!controlExists ? (
                <Typography variant="body2" color="text.secondary" data-testid="controls-empty-action">
                  No control recorded yet.
                </Typography>
              ) : null}
              <TextField
                label="Description"
                multiline
                minRows={2}
                value={draft.description}
                onChange={(event) => updateDraft({ description: event.target.value })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Owner"
                  value={draft.owner}
                  onChange={(event) => updateDraft({ owner: event.target.value })}
                  fullWidth
                />
                <TextField
                  select
                  label="Status"
                  value={draft.status}
                  onChange={(event) => updateDraft({ status: event.target.value as ControlActionStatusUi })}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="planned">{formatControlStatusLabel('planned')}</MenuItem>
                  <MenuItem value="in_progress">{formatControlStatusLabel('in_progress')}</MenuItem>
                  <MenuItem value="implemented">{formatControlStatusLabel('implemented')}</MenuItem>
                  <MenuItem value="needs_followup">{formatControlStatusLabel('needs_followup')}</MenuItem>
                </TextField>
                <TextField
                  label="Due date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={draft.dueDate}
                  onChange={(event) => updateDraft({ dueDate: event.target.value })}
                  fullWidth
                />
              </Stack>
              {draft.status === 'implemented' ? (
                <TextField
                  label="Implemented at"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={draft.implementedAt}
                  onChange={(event) => updateDraft({ implementedAt: event.target.value })}
                  inputProps={{ 'data-testid': 'controls-implemented-at' }}
                  helperText="When the control was put in place. Required. Not inferred from scan or save time."
                />
              ) : null}
              <TextField
                label="Note (optional)"
                multiline
                minRows={2}
                value={draft.note}
                onChange={(event) => updateDraft({ note: event.target.value })}
              />
              <Button
                variant="contained"
                onClick={() => void handleSaveControl()}
                disabled={pendingKey === 'save-control'}
              >
                {recordedAction ? 'Update control' : 'Save control'}
              </Button>
              {success === 'Control saved' ? (
                <Alert severity="success" data-testid="controls-save-success-inline">
                  Control saved
                </Alert>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              <Typography variant="body2" color="text.secondary">
                {draft.description || 'No control recorded yet.'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Control owner: {draft.owner || 'Unassigned'} · Status: {formatControlStatusLabel(draft.status)}
                {draft.dueDate ? ` · Due: ${formatDateTime(draft.dueDate, 'Not recorded', siteTimeZone)}` : ''}
              </Typography>
              {(recordedAction as WorkflowCorrectiveAction | undefined)?.implemented_at
                || recordedAction?.implemented_at ? (
                <Typography variant="body2" color="text.secondary" data-testid="controls-implemented-at-label">
                  {`Implemented ${formatDateTime(
                    (recordedAction as WorkflowCorrectiveAction | undefined)?.implemented_at
                    || recordedAction?.implemented_at,
                    'Not recorded',
                    siteTimeZone,
                  )}`}
                </Typography>
              ) : null}
            </Stack>
          )}
          <Accordion disableGutters elevation={0} sx={{ ...detailsSx, mt: 1 }} data-testid="controls-more-options">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>More options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {correctiveActionsSlot}
                {!readOnly ? (
                  <Stack spacing={1.5} data-testid="controls-advanced-decision">
                    <TextField
                      select
                      label="Decision"
                      value={draft.siteDecision}
                      onChange={(event) => updateDraft({ siteDecision: event.target.value as ControlSiteDecisionUi })}
                    >
                      <MenuItem value="accepted">Accepted</MenuItem>
                      <MenuItem value="not_proceeding">Not proceeding</MenuItem>
                    </TextField>
                    {draft.siteDecision === 'not_proceeding' ? (
                      <TextField
                        label="Note (optional)"
                        value={draft.decisionNote}
                        onChange={(event) => updateDraft({ decisionNote: event.target.value })}
                      />
                    ) : null}
                    {reviewJson ? (
                      <Box data-testid="controls-review-json-details">
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Control review details
                        </Typography>
                        <Typography
                          component="pre"
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
                        >
                          {reviewJson}
                        </Typography>
                      </Box>
                    ) : null}
                  </Stack>
                ) : null}
                {!readOnly && primaryRecommendation ? (
                  <Stack spacing={1.5}>
                    <TextField
                      label="Link existing control"
                      value={draft.linkActionId}
                      onChange={(event) => updateDraft({ linkActionId: event.target.value })}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => void handleLinkCorrectiveAction()}
                      disabled={pendingKey === 'link-action'}
                    >
                      Link existing control
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
          {!readOnly ? (
            <Accordion disableGutters elevation={0} sx={detailsSx} data-testid="controls-advanced-actions">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>More actions / Advanced</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <TextField
                    label="Recommendation text"
                    multiline
                    minRows={2}
                    value={draft.addRecommendationText}
                    onChange={(event) => updateDraft({ addRecommendationText: event.target.value })}
                  />
                  <TextField
                    label="Rationale"
                    value={draft.addRecommendationRationale}
                    onChange={(event) => updateDraft({ addRecommendationRationale: event.target.value })}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      select
                      label="Priority"
                      value={draft.addRecommendationPriority}
                      onChange={(event) => updateDraft({ addRecommendationPriority: event.target.value })}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </TextField>
                    <Button
                      variant="outlined"
                      onClick={() => void handleCreateRecommendation()}
                      disabled={pendingKey === 'create'}
                    >
                      Add recommendation
                    </Button>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid="controls-follow-up">
        <CardContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Follow-up</Typography>
          <Typography variant="body2" color="text.secondary" data-testid="controls-follow-up-sentence">
            {followUpSummary || followUp.sentence}
          </Typography>
          <Accordion disableGutters elevation={0} sx={{ ...detailsSx, mt: 1 }} data-testid="controls-follow-up-details">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {followUpSlot || (
                <Typography variant="body2" color="text.secondary">Follow-up measurement will appear here.</Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default SafetyCaseRecommendationsSection;
